// One way to show a failure: what happened, what SAM did, what to do next.
//
// The words come from the server (`shared::failure_copy`, as `FailureView`
// beside every `last_error`), so the campaign, creative and audience screens,
// the alert email and the batch-worker's suggested action all say the same
// thing about the same failure. This component only lays them out.
//
// Before it, the operator read the machine string itself — on 2026-10-07,
// "upstream provider=meta operation=campaign_readback object=campaign
// field=pause_readback retryable=false error_kind=mismatch unavailable" — or a
// browser-side template that turned it into a sentence about "could not
// publish" on a campaign that had published days earlier. The string is still
// here, under "Technical details", because support needs it; it is just no
// longer the first thing anyone reads.

import type { FailureView } from '@/api/types.gen';

export function FailureNotice({
  failure,
  fallback,
  onRetry,
  retrying = false,
  dense = false,
  announce = true,
  tone = 'bad',
}: {
  /** The server's words. Absent on an older response or a synthetic failure. */
  failure?: FailureView | null;
  /** What to show when there are no words: the raw text, as before. */
  fallback?: string | null;
  /** Offered when the failure says Retry will help (`retry` is not `none`). */
  onRetry?: () => void;
  retrying?: boolean;
  dense?: boolean;
  /** `false` inside a container that is already a live region. */
  announce?: boolean;
  /** `warn` for a failure that did no harm yet (the email went out; only a record or a
   * confirmation is missing): the headline is not painted as an error. */
  tone?: 'bad' | 'warn';
}) {
  const fontSize = dense ? 11.5 : 12.5;
  const canRetry = Boolean(onRetry) && failure?.retry !== 'none';
  if (!failure) {
    // No words from the server (an older response, or a failure the browser
    // made up itself): show the text as it always was, rather than a generic
    // headline over it.
    if (!fallback) return null;
    return (
      <div
        className="failure-notice"
        role={announce ? 'alert' : undefined}
        data-failure-code="none"
        style={{ fontSize, lineHeight: 1.45, overflowWrap: 'anywhere' }}
      >
        <span style={{ color: 'var(--bad)' }}>{fallback}</span>
        {canRetry && (
          <button type="button" className="btn btn-sm" style={{ marginLeft: 8 }}
                  disabled={retrying} onClick={onRetry}>
            {retrying ? 'Retrying…' : 'Retry'}
          </button>
        )}
      </div>
    );
  }
  const technical = failure.technical || fallback || '';
  return (
    <div
      className="failure-notice"
      role={announce ? 'alert' : undefined}
      data-failure-code={failure.code}
      style={{ fontSize, lineHeight: 1.45, overflowWrap: 'anywhere' }}
    >
      <div
        className="failure-notice-headline"
        style={{ fontWeight: 600, color: tone === 'warn' ? 'var(--ink)' : 'var(--bad)' }}
      >
        {failure.headline}
      </div>
      {failure.body && <div className="failure-notice-body" style={{ marginTop: 2 }}>{failure.body}</div>}
      {failure.provider_said && (
        <div className="failure-notice-provider" style={{ marginTop: 4 }}>
          {/* The provider's own sentence, re-sanitized server-side. Quoted so
              it reads as theirs, not ours. */}
          {failure.provider ?? 'The ad platform'} said: &ldquo;{failure.provider_said}&rdquo;
        </div>
      )}
      {failure.provider_messages && failure.provider_messages.length > 0 && (
        <div className="failure-notice-provider" style={{ marginTop: 4 }}>
          {/* Several messages (Xero's ValidationErrors), one per line, each
              re-sanitized server-side. */}
          {failure.provider ?? 'The ad platform'} said:
          <ul style={{ margin: '2px 0 0', paddingLeft: 18 }}>
            {failure.provider_messages.map((message, index) => (
              <li key={index}>&ldquo;{message}&rdquo;</li>
            ))}
          </ul>
        </div>
      )}
      {failure.next_step && (
        <div className="failure-notice-next" style={{ marginTop: 4 }}>
          <b>Next step:</b> {failure.next_step}
        </div>
      )}
      {canRetry && (
        <button
          type="button"
          className="btn btn-sm"
          style={{ marginTop: 6 }}
          disabled={retrying}
          onClick={onRetry}
        >
          {retrying ? 'Retrying…' : 'Retry'}
        </button>
      )}
      {technical && (
        <details className="failure-notice-technical" style={{ marginTop: 6 }}>
          <summary className="muted" style={{ cursor: 'pointer' }}>Technical details</summary>
          <div className="mono" style={{ fontSize: 10.5, marginTop: 4, opacity: 0.85 }}>
            {technical}
          </div>
        </details>
      )}
    </div>
  );
}
